import React, { useState } from "react";
import { useCreateCallFlowMutation } from "@/store/rest_api";
import TemplateGallery, { TemplateChoice } from "./components/TemplateGallery";
import { toast } from "sonner";
import { CallFlowType } from "@/model/interface/CallFlowType";
import { apiErrorMessage } from "@/util/apiError";
import CallFlowEditor, { CallFlowPersistPayload, EditorTab } from "./components/CallFlowEditor";
import { useCollapsedSidebar } from "@/hooks/useCollapsedSidebar";

export default function CreateCallFlow() {
  const [createCallFlow, { isLoading }] = useCreateCallFlowMutation();
  const [activeTab, setActiveTab] = useState<EditorTab>("details");
  useCollapsedSidebar(activeTab === "flow");
  const [hasChosenStart, setHasChosenStart] = useState(false);
  const [templateChoice, setTemplateChoice] = useState<TemplateChoice | null>(null);

  const persistFlow = async ({ values, active, nodes, edges, actions }: CallFlowPersistPayload) => {
    try {
      await createCallFlow({
        name: values.name,
        description: values.description,
        type: values.type as CallFlowType,
        phoneNumber: values.phoneNumberId ? { id: values.phoneNumberId } : undefined,
        flowData: {
          nodes,
          edges,
        },
        actions,
        active,
        callerIdLookupMode: values.callerIdLookupMode,
        ringbackAudioClipId: values.ringbackAudioClipId ?? null,
        holdAudioClipId: values.holdAudioClipId ?? null,
        screenUnknownCallers: values.screenUnknownCallers,
      }).unwrap();

      toast.success(active ? "Call flow created successfully" : "Call flow created and turned off");
      return true;
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to create call flow"));
      return false;
    }
  };

  if (!hasChosenStart) {
    return (
      <TemplateGallery
        onChoose={(choice) => {
          setTemplateChoice(choice);
          setHasChosenStart(true);
        }}
      />
    );
  }

  return (
    <CallFlowEditor
      title="Create Call Flow"
      subtitle="Design how incoming calls are handled"
      submitLabel="Create Call Flow"
      isSaving={isLoading}
      onActiveTabChange={setActiveTab}
      initialNodes={templateChoice?.nodes}
      initialEdges={templateChoice?.edges}
      initialValues={
        templateChoice
          ? {
              name: "",
              description: "",
              type: templateChoice.type,
              phoneNumberId: "",
              callerIdLookupMode: "OFF",
              ringbackAudioClipId: null,
              holdAudioClipId: null,
              screenUnknownCallers: false,
              active: true,
            }
          : undefined
      }
      onPersist={persistFlow}
    />
  );
}
